<template>
  <div class="container is-fluid" ref="container">
    <section class="section">
      <div class="mb-4 is-flex is-align-items-center">
        <span class="is-size-4 has-text-weight-bold">
          {{ $t("bnpl:limit.title.edit") }}
          {{ `#${id}` }}
        </span>

        <div class="ml-auto">
          <button
            @click="cancelLimit"
            class="button is-size-7 has-text-danger is-text"
          >
            Cancel
          </button>
        </div>
      </div>

      <div class="columns">
        <div class="column is-three-quarters">
          <div v-if="loaded" class="entity-details-card">
            <div class="is-flex is-justify-content-space-between mb-4">
              <p class="mb-auto has-text-weight-bold">
                {{ $t("bnpl:limit.limit_data") }}
              </p>
              <div class="tag-list">
                <span class="tag is-rounded is-large is-blue">
                  {{ $t(LIMIT_SOURCE_TOKENS[limitData.source]) }}
                </span>
                <span
                  class="tag is-rounded is-large"
                  :class="getTagColor(limitData.status)"
                >
                  {{ $t(BNPL_LIMIT_STATUS_TOKENS[limitData.status]) }}
                </span>
              </div>
            </div>

            <div class="entity-details-card-row">
              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("bnpl:limit.header.customer_name") }}
                </p>
                <span class="has-text-weight-medium">
                  {{
                    limitData.firstName || limitData.lastName
                      ? `${limitData.lastName} ${limitData.firstName}`
                      : "-"
                  }}
                </span>
              </div>

              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("bnpl:limit.header.created_at") }}
                </p>
                <span class="has-text-weight-medium">
                  {{ localizedFormatter(country.languageIso).dateTime(limitData.createdAt) }}
                </span>
              </div>

              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("bnpl:limit.header.expiration_date") }}
                </p>
                <span class="has-text-weight-medium">
                  {{ localizedFormatter(country.languageIso).dateTime(limitData.expirationDate) }}
                </span>
              </div>

              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("bnpl:limit.header.limit_value") }}
                </p>
                <span class="has-text-weight-medium">
                  {{ localizedFormatter(country.languageIso).money(limitData.currencyIso, limitData.limitValue) }}
                </span>
              </div>
            </div>
          </div>

          <b-skeleton v-if="!loaded" height="140"></b-skeleton>
        </div>
        <div class="column">
          <div v-if="loaded" class="entity-details-card">
            <!-- <div class="is-flex is-justify-content-space-between mb-4">
            </div> -->
            <p class="m-0 has-text-weight-bold">
              {{ $t("bnpl:limit.customer_data") }}
            </p>

            <div class="is-flex is-flex-direction-column">
              <div class="mt-4">
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("bnpl:limit.customer_data.header.name") }}
                </p>
                <span v-if="customer.lastName || customer.firstName" class="has-text-weight-medium">
                  {{ customer.lastName }} {{ customer.firstName }}
                </span>
                <span v-else class="has-text-weight-medium">
                  -
                </span>
              </div>
              <div class="mt-4">
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("bnpl:limit.customer_data.header.email") }}
                </p>
                <span v-if="customer.email" class="has-text-weight-medium">
                  {{ customer.email }}
                </span>
                <span v-else class="has-text-weight-medium">
                  -
                </span>
              </div>
              <div class="mt-4">
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("bnpl:limit.customer_data.header.phone") }}
                </p>
                <span v-if="customer.phone" class="has-text-weight-medium">
                  {{ customer.phone }}
                </span>
                <span v-else class="has-text-weight-medium">
                  -
                </span>
              </div>
            </div>
          </div>

          <b-skeleton v-if="!loaded" height="140"></b-skeleton>
        </div>
      </div>
    </section>

    <section class="section">
      <b-tabs v-model="activeTab" class="mb-0 mt-4">
        <b-tab-item
          :value="TABS.LIMIT_LANES"
          :label="$t('bnpl:limit.tab.limit_lanes')"
        />
      </b-tabs>

      <!-- ALAPADATOK -->
      <template v-if="loaded">
        <template v-if="activeTab === TABS.LIMIT_LANES">
          <BnplLimitLaneList :currency="limitData.currencyIso" />
        </template>
      </template>

      <b-skeleton v-else height="80"></b-skeleton>
    </section>
  </div>
</template>

<script>
import {
  BNPL_LIMIT_STATUS,
  BNPL_LIMIT_STATUS_TOKENS,
  LIMIT_SOURCE_TOKENS
} from "@/utils/const";
import { formatMoney, formatDateTime, localizedFormatter } from "@/utils/util";
import { mapGetters } from "vuex/dist/vuex.common.js";

const TABS = {
  LIMIT_LANES: "LIMIT_LANES",
  BASE_DATA: "BASE_DATA",
  PSD2: "PSD2",
  SCORING: "SCORING",
  HISTORY: "HISTORY",
};

export default {
  name: "BnplLimit",
  title: "pp:page.limit.title",
  components: {
    BnplLimitLaneList: () => import("@/components/limit/BnplLimitLaneList.vue"),
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      TABS,
      BNPL_LIMIT_STATUS_TOKENS,
      LIMIT_SOURCE_TOKENS,
      activeTab: TABS.LIMIT_LANES,
      limitData: {},
      customer: {},
      loaded: false,
    };
  },
  computed: {
    ...mapGetters({
      country: "i18n/country",
    }),
    scoreIds() {
      return this.limitData.limitLanes.map((limitLane) => limitLane.scoreId);
    },
  },
  async created() {
    await this.fetch();
    this.loaded = true;
  },
  methods: {
    formatDateTime,
    formatMoney,
    localizedFormatter,
    // formatDate,
    async fetch() {
      await this.$store.dispatch("limit/fetchOne", this.id);
      this.limitData = this.$store.getters["limit/get"];

      this.activeTab = TABS.LIMIT_LANES;

      if (!!this.limitData.customerId) {
        await this.$store.dispatch(
          "customer/fetchOne",
          this.limitData.customerId
        );
        this.customer = this.$store.getters["customer/get"](
          this.limitData.customerId
        );
      }
    },
    getTagColor(status) {
      if ([BNPL_LIMIT_STATUS.ACTIVE].includes(status)) {
        return "is-success";
      } else if (
        [
          BNPL_LIMIT_STATUS.CANCELLED,
          BNPL_LIMIT_STATUS.DENIED,
          BNPL_LIMIT_STATUS.INTERRUPTED,
        ].includes(status)
      ) {
        return "is-danger";
      } else if ([BNPL_LIMIT_STATUS.EXPIRED].includes(status)) {
        return "is-warning";
      }
    },
    async cancelLimit() {
      await this.$store.dispatch("limit/cancelLimit", this.id);
      await this.$store.dispatch("limit/fetchOne", this.id);
      this.limitData = this.$store.getters["limit/get"];
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/colors.scss";

.entity-details-card {
  .partner-logo {
    height: 25px;
  }

  // gap: 16px;
}

.entity-details-card-row {
  gap: 16px;
}

.tag-list {
  display: flex;
  flex-direction: row;
  gap: 16px;
}
</style>
